import { useState, useRef, useEffect } from 'react'
import { MoreVertical, Pencil, Copy, Trash, Trash2 } from 'lucide-react'
import type { Workspace } from '@/service/api'
import { useAppStore } from '@/store'
import { formatRelativeTime } from '@/utils/timeUtils'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import InlineRenameInput from '@/components/InlineRenameInput'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuTrigger,
} from '@/components/intueri/IntuiDropdownMenu'

interface BoardTableV2Props {
  boards: Workspace[]
  onBoardClick: (id: string) => void
  onBoardDelete: (id: string) => void
  onBoardHardDelete: (id: string) => void
}

export default function BoardTableV2({ boards, onBoardClick, onBoardDelete, onBoardHardDelete }: BoardTableV2Props) {
  const { t } = useTranslation()
  const { renameBoard, duplicateBoard } = useAppStore()
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const renameSavingRef = useRef(false)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus()
      renameInputRef.current.select()
    }
  }, [renamingId])

  useEffect(() => {
    return () => { if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current) }
  }, [])

  const handleRenameStart = (board: Workspace) => {
    setRenamingId(board.id)
    setRenameValue(board.name)
    renameSavingRef.current = false
  }

  const handleRenameSave = async () => {
    if (renameSavingRef.current) return
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null }
    if (!renamingId || !renameValue.trim()) {
      setRenamingId(null)
      return
    }
    renameSavingRef.current = true
    try {
      await renameBoard(renamingId, renameValue.trim())
    } finally {
      setRenamingId(null)
      renameSavingRef.current = false
    }
  }

  const handleRenameBlur = () => {
    blurTimeoutRef.current = setTimeout(handleRenameSave, 150)
  }

  const handleRenameCancel = () => {
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null }
    setRenamingId(null)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRenameSave()
    if (e.key === 'Escape') handleRenameCancel()
  }

  // Empty state is owned by BoardListV2 (FS-553), which distinguishes
  // "no boards" from "no search results"; this component only renders rows.
  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center border-b border-[#e5e5e5] dark:border-dark-border px-2 py-4">
        <span className="flex-1 text-sm font-medium text-muted-foreground">
          {t('board:table.name', 'Name')}
        </span>
        <span className="w-36 text-sm font-medium text-muted-foreground">
          {t('board:table.last_viewed', 'Last Viewed')}
        </span>
        <span className="w-9" />
      </div>

      {/* Rows */}
      {boards.map((board) => (
        <div
          key={board.id}
          className="flex h-[52px] items-center border-b border-[#e5e5e5] dark:border-dark-border px-2 hover:bg-muted/50 transition-colors cursor-pointer"
          onClick={() => renamingId !== board.id && onBoardClick(board.id)}
        >
          {/* Name cell
               UI-53: every row here IS a board, so this icon repeated
               identically down the whole column and distinguished nothing —
               noise with no information. The grid cards carry no icon either,
               so dropping it also makes the two view modes agree. */}
          <div className="flex flex-1 items-center gap-2 min-w-0">
            {renamingId === board.id ? (
              // Same typography as the display span, so the row keeps its
              // exact footprint while renaming (Enter/blur commits, Escape
              // cancels — no separate confirm button).
              <InlineRenameInput
                ref={renameInputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={handleRenameBlur}
                onKeyDown={handleRenameKeyDown}
                onClick={(e) => e.stopPropagation()}
                className="text-sm font-medium text-foreground dark:text-gray-100 truncate"
              />
            ) : (
              <span className="text-sm font-medium text-foreground dark:text-gray-100 truncate">
                {board.name}
              </span>
            )}
          </div>

          {/* Events/time cell */}
          <span className="w-36 text-sm text-muted-foreground shrink-0">
            {formatRelativeTime(board.updated_at)}
          </span>

          {/* Actions cell */}
          <div className="w-9 flex justify-center" onClick={(e) => e.stopPropagation()}>
            <IntuiDropdownMenu>
              <IntuiDropdownMenuTrigger asChild>
                <IntuiButton variant="ghost" size="icon-sm" aria-label={t('board:actions.more', 'More actions')}>
                  <MoreVertical className="size-4 text-muted-foreground" />
                </IntuiButton>
              </IntuiDropdownMenuTrigger>
              <IntuiDropdownMenuContent align="end">
                <IntuiDropdownMenuItem
                  icon={<Pencil />}
                  onClick={() => handleRenameStart(board)}
                >
                  {t('board:actions.rename', 'Rename')}
                </IntuiDropdownMenuItem>
                <IntuiDropdownMenuItem
                  icon={<Copy />}
                  onClick={async () => {
                    const result = await duplicateBoard(board.id)
                    if (result) {
                      toast.success(t('board:actions.duplicate_success', 'File duplicated in your workspace'))
                    }
                  }}
                >
                  {t('board:actions.duplicate', 'Duplicate')}
                </IntuiDropdownMenuItem>
                {/* FS-609: Archive is disabled everywhere until the archive/restore
                    flow is built — today it would hard-delete the board (same as
                    Delete) behind "moved to archive" copy. Wiring (onBoardDelete)
                    is kept intact so re-enabling is just removing `disabled`. */}
                <IntuiDropdownMenuItem
                  variant="destructive"
                  icon={<Trash />}
                  onClick={() => onBoardDelete(board.id)}
                  disabled
                >
                  {t('board:actions.archive', 'Archive')}
                </IntuiDropdownMenuItem>
                <IntuiDropdownMenuItem
                  variant="destructive"
                  icon={<Trash2 />}
                  onClick={() => onBoardHardDelete(board.id)}
                >
                  {t('board:actions.delete', 'Delete')}
                </IntuiDropdownMenuItem>
              </IntuiDropdownMenuContent>
            </IntuiDropdownMenu>
          </div>
        </div>
      ))}
    </div>
  )
}
